import { useState } from 'react';
import { Layers, Loader2, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { spatialApi, getErrorMessage } from '../../lib/api';

interface OverlapAlertProps {
    polygonCount: number;
}

export default function OverlapAlert({ polygonCount }: OverlapAlertProps) {
    const [overlaps, setOverlaps] = useState<any[]>([]);
    const [showOverlaps, setShowOverlaps] = useState(false);
    const [detecting, setDetecting] = useState(false);

    const handleDetect = async () => {
        setDetecting(true);
        try {
            const result = await spatialApi.detectOverlaps();
            setOverlaps(result.overlaps || []);
            setShowOverlaps(true);
            if (result.total_overlaps === 0) {
                toast.success('No se detectaron superposiciones');
            } else {
                toast.error(
                    `${result.total_overlaps} superposición(es) detectada(s): ${result.total_overlap_area_ha} ha`
                );
            }
        } catch (error: any) {
            toast.error(getErrorMessage(error) || 'Error al detectar superposiciones');
        } finally {
            setDetecting(false);
        }
    };

    return (
        <>
            <button
                onClick={handleDetect}
                disabled={detecting || polygonCount < 2}
                className="btn-secondary flex items-center gap-2 text-sm"
                title="Detectar superposiciones entre polígonos"
            >
                {detecting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Layers className="w-4 h-4" />
                )}
                Superposiciones
            </button>

            {showOverlaps && overlaps.length > 0 && (
                <button
                    onClick={() => setShowOverlaps(false)}
                    className="btn-secondary flex items-center gap-2 text-sm text-red-600 border-red-200"
                >
                    <AlertTriangle className="w-4 h-4" />
                    {overlaps.length} superposición(es) — Ocultar
                </button>
            )}

            {showOverlaps && overlaps.length > 0 && (
                <div className="card p-4 col-span-full">
                    <h3 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {overlaps.length} Superposición(es) Detectada(s)
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                        {overlaps.map((o: any, idx: number) => (
                            <div
                                key={idx}
                                className="flex items-center justify-between text-sm p-2 bg-red-50 dark:bg-red-900/20 rounded-lg"
                            >
                                <div>
                                    <span className="font-medium">{o.polygon_a_name}</span>
                                    <span className="text-gray-500 mx-1">×</span>
                                    <span className="font-medium">{o.polygon_b_name}</span>
                                </div>
                                <span className="text-red-600 font-semibold">
                                    {o.overlap_area_ha} ha
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}
